import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, Info, RefreshCcw, Save } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Tooltip } from "@/components/ui/Tooltip";
import {
  useConnectionByProject,
  useFormatkaTemplates,
  useParcels,
  useReseedSheetSet,
  useSaveSheetFrames,
  useSheetSet,
  useUpdateSheetSet,
  useVariants,
} from "../hooks";
import type { FormatkaType, Parcel, SheetFrame } from "../types";
import { FORMATKA_MODE_LABEL, FORMATKA_PAPER_LABEL, FORMATKA_TYPE_LABEL, SHEET_SET_STATUS_LABEL } from "../types";
import { operatorOptionBadge, sortByOperator } from "../operatorTemplates";
import { apiErr } from "../utils";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  FrameMapEditor,
  type AddFrameOpts,
  type EditableFrame,
} from "../components/formatki/FrameMapEditor";
import { SCALE_OPTIONS, snapFrameCenter } from "../components/formatki/frameGeometry";
import { SheetListPanel } from "../components/formatki/SheetListPanel";
import { GenerateFormatkiModal } from "../components/formatki/GenerateFormatkiModal";
import { SheetSetParcelsInfo } from "../components/formatki/SheetSetParcelsInfo";

let uidCounter = 0;
const mintUid = () => `f-${Date.now().toString(36)}-${++uidCounter}`;

const toEditable = (frames: SheetFrame[]): EditableFrame[] =>
  frames.map((f) => ({ ...f, uid: mintUid() }));

const toSheetFrames = (frames: EditableFrame[]): SheetFrame[] =>
  [...frames]
    .sort((a, b) => a.order - b.order)
    .map(({ uid: _uid, ...f }, i) => ({ ...f, order: i + 1 }));

/** „Rozplanowanie arkuszy" — the intermediate step between the auto-proposed
 *  seed and generation: move/rotate/add/remove frames on the map, reorder
 *  sheet numbering, then save (PUT full-replace) and generate. All geometry in
 *  PUWG meters end-to-end (frames = future DXF viewports 1:1). */
export function FormatkiPlanPage() {
  const { id: projectId, sheetSetId } = useParams<{ id: string; sheetSetId: string }>();
  const { data: connection, isLoading: connLoading } = useConnectionByProject(projectId);
  const { data: sheetSet, isLoading: setLoading } = useSheetSet(sheetSetId);
  const { data: parcels } = useParcels(connection?.id);
  const { data: variants } = useVariants(connection?.id);
  const { data: templates } = useFormatkaTemplates();

  const saveFrames = useSaveSheetFrames(sheetSetId ?? "");
  const reseed = useReseedSheetSet(sheetSetId ?? "");
  const updateSet = useUpdateSheetSet(sheetSetId ?? "");

  const [frames, setFrames] = useState<EditableFrame[]>([]);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  // The set-level default scale; frames whose scale equals it "inherit" and are
  // re-scaled when it changes; frames with a different scale are overrides.
  const [setScale, setSetScale] = useState(500);
  const [dirty, setDirty] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initializedForRef = useRef<string | null>(null);

  // Load server frames into local editor state ONCE per sheet set (edits are
  // local until „Zapisz rozmieszczenie"; reseed replaces wholesale).
  useEffect(() => {
    if (!sheetSet || initializedForRef.current === sheetSet.id) return;
    initializedForRef.current = sheetSet.id;
    setFrames(toEditable(sheetSet.frames));
    setSetScale(sheetSet.scale_denominator);
    setDirty(false);
  }, [sheetSet]);

  // Unsaved-changes guard (browser navigation / tab close).
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const routeVersion = useMemo(
    () =>
      (variants ?? [])
        .flatMap((v) => v.versions)
        .find((ver) => ver.id === sheetSet?.route_version),
    [variants, sheetSet?.route_version],
  );
  const template = useMemo(
    () => (templates ?? []).find((t) => t.id === sheetSet?.template),
    [templates, sheetSet?.template],
  );
  // Szablony pod operatora projektu na górze listy — nic nie znika, zmienia się
  // tylko kolejność (ta sama reguła co przy szablonach ODT).
  const templateOptions = useMemo(
    () => sortByOperator(templates ?? [], connection?.operator ?? ""),
    [templates, connection?.operator],
  );

  const patchFrame = useCallback(
    (uid: string, patch: Partial<Omit<EditableFrame, "uid">>) => {
      setFrames((prev) => prev.map((f) => (f.uid === uid ? { ...f, ...patch } : f)));
      setDirty(true);
    },
    [],
  );

  // A private-parcel set („Działka") belongs to exactly one parcel: a sheet added by
  // hand inherits it, so the WPW package slicing keeps the sheet with the parcel's
  // own (the backend stamps it on save as well; this keeps the list badge honest).
  // A set without frames (layout failure) still knows its parcel from source_parcel_ids.
  const setParcelId =
    sheetSet?.mode === "parcel"
      ? (sheetSet.frames.find((f) => f.parcel_id)?.parcel_id ?? sheetSet.source_parcel_ids?.[0] ?? null)
      : null;

  // Which działka(i) this view is for — and whose land. Sets are created several at once
  // (one per parcel / one per urząd) and their editors looked identical („Działka"), so
  // the header names the parcel(s) and owners, resolved from the project's parcel list.
  // Office tiles carry no parcel_id — their parcels come from source_parcel_ids.
  const setParcels = useMemo(() => {
    if (!sheetSet || !parcels) return [];
    const ids =
      sheetSet.mode === "parcel"
        ? (setParcelId ? [setParcelId] : [])
        : sheetSet.mode === "parcel_office"
          ? (sheetSet.source_parcel_ids ?? [])
          : [];
    const byId = new Map(parcels.map((p) => [p.id, p]));
    return ids.map((id) => byId.get(id)).filter((p): p is Parcel => !!p);
  }, [sheetSet, parcels, setParcelId]);
  // Falls back to the serializer's label while the parcel list loads (or lacks it).
  const parcelNumber =
    sheetSet?.mode === "parcel"
      ? (setParcels[0] ? setParcels[0].short_number || setParcels[0].teryt : sheetSet.parcel_label) || null
      : null;

  usePageTitle(
    parcelNumber
      ? `Formatki — dz. ${parcelNumber}`
      : sheetSet?.name
        ? `Formatki — ${sheetSet.name}`
        : "Formatki — rozplanowanie arkuszy",
  );

  const addFrame = useCallback(
    (centerPuwg: [number, number], opts?: AddFrameOpts) => {
      setFrames((prev) => {
        const uid = mintUid();
        setSelectedUid(uid);
        return [
          ...prev,
          {
            uid,
            order: prev.length + 1,
            center: centerPuwg,
            rotation_deg: opts?.rotation_deg ?? 0,
            scale_denominator: opts?.scale_denominator ?? setScale,
            parcel_id: setParcelId,
            label: opts?.label ?? "",
          },
        ];
      });
      setDirty(true);
    },
    [setScale, setParcelId],
  );

  // „Obróć 90°” toggles the selected frame between poziom/pion (0 ↔ 90).
  const rotateSelected = useCallback(() => {
    const f = frames.find((x) => x.uid === selectedUid);
    if (!f) return;
    const rotated = Math.round(f.rotation_deg / 90) % 2 !== 0;
    patchFrame(f.uid, { rotation_deg: rotated ? 0 : 90 });
  }, [frames, selectedUid, patchFrame]);

  // Set-level scale: re-scale inheriting frames (scale == current set scale),
  // leave per-frame overrides; persist the new default on the sheet set.
  const applySetScale = (newScale: number) => {
    setFrames((prev) =>
      prev.map((f) => (f.scale_denominator === setScale ? { ...f, scale_denominator: newScale } : f)),
    );
    setSetScale(newScale);
    setDirty(true);
    updateSet.mutate({ scale_denominator: newScale });
  };

  const removeFrame = useCallback((uid: string) => {
    setFrames((prev) =>
      prev
        .filter((f) => f.uid !== uid)
        .sort((a, b) => a.order - b.order)
        .map((f, i) => ({ ...f, order: i + 1 })),
    );
    setSelectedUid((cur) => (cur === uid ? null : cur));
    setDirty(true);
  }, []);

  const reorderFrames = useCallback((uids: string[]) => {
    setFrames((prev) => {
      const byUid = new Map(prev.map((f) => [f.uid, f]));
      return uids
        .map((uid, i) => ({ ...byUid.get(uid)!, order: i + 1 }))
        .filter(Boolean);
    });
    setDirty(true);
  }, []);

  const doSave = (onDone?: () => void) => {
    setError(null);
    saveFrames.mutate(toSheetFrames(frames), {
      onSuccess: (set) => {
        // Preserve uids by index — the response is our list renumbered.
        setFrames((prev) => {
          const orderedPrev = [...prev].sort((a, b) => a.order - b.order);
          return set.frames.map((f, i) => ({ ...f, uid: orderedPrev[i]?.uid ?? mintUid() }));
        });
        setDirty(false);
        onDone?.();
      },
      onError: (e) => setError(apiErr(e)),
    });
  };

  const doReseed = () => {
    // Bez ramek nie ma czego zastępować — pytanie byłoby tylko przeszkodą (zestaw
    // z błędem rozplanowania z wsadu per działka).
    if (frames.length > 0 && !window.confirm("Zastąpić obecne rozmieszczenie nową propozycją automatyczną?")) return;
    setError(null);
    reseed.mutate(undefined, {
      onSuccess: (set) => {
        setFrames(toEditable(set.frames));
        setSelectedUid(null);
        setDirty(false);
      },
      onError: (e) => setError(apiErr(e)),
    });
  };

  const openGenerate = () => {
    if (frames.length === 0) return;
    if (dirty) doSave(() => setShowGenerate(true));
    else setShowGenerate(true);
  };

  if (connLoading || setLoading) {
    return (
      <div className="p-10 flex justify-center"><Spinner /></div>
    );
  }
  if (!connection || !sheetSet) {
    return (
      <p className="p-10 text-center text-sm text-[var(--color-text-muted)]">
        Nie znaleziono zestawu formatek.
      </p>
    );
  }

  const busy = saveFrames.isPending || reseed.isPending || sheetSet.status === "generating";
  // Parcel / office sets carry the działka + owners strip — the map and sidebar give
  // up its height so the page itself does not start scrolling.
  const hasParcelInfo = sheetSet.mode === "parcel" || sheetSet.mode === "parcel_office";
  const bodyHeight = hasParcelInfo ? "h-[calc(100vh-260px)]" : "h-[calc(100vh-190px)]";
  const output = sheetSet.status === "success" ? sheetSet.output_file : null;

  return (
    <div className="p-4 lg:p-6 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to={`/power-projects/${projectId}?tab=map`}
          className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
        >
          <ArrowLeft size={14} /> {connection.project_name}
        </Link>
        <h1 className="text-sm font-bold uppercase tracking-widest font-mono text-[var(--color-text-primary)]">
          Formatki — rozplanowanie arkuszy
        </h1>
        <span className="text-[11px] font-mono text-[var(--color-text-muted)]">
          {[
            // A per-parcel set names its działka in the strip below — the mode label
            // would only repeat „Działka"; a custom name still shows.
            sheetSet.name || (sheetSet.mode === "parcel" ? "" : FORMATKA_MODE_LABEL[sheetSet.mode]),
            sheetSet.route_version_label ? `trasa ${sheetSet.route_version_label}` : "",
            SHEET_SET_STATUS_LABEL[sheetSet.status],
            template?.name ?? "",
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {/* A bundle is a merge of finished sets — re-seeding it has no meaning and
              would wipe the per-działka numbering (blocked server-side too). */}
          {sheetSet.mode !== "bundle" && (
            <Button size="sm" variant="ghost" disabled={busy} onClick={doReseed} title="Zastąp rozmieszczenie nową propozycją">
              <RefreshCcw size={13} className="mr-1" /> Zaproponuj ponownie
            </Button>
          )}
          <Button size="sm" variant="secondary" disabled={busy || !dirty} loading={saveFrames.isPending} onClick={() => doSave()}>
            <Save size={13} className="mr-1" /> Zapisz rozmieszczenie
          </Button>
          {/* Once a ZIP exists, "Generuj" stops being the primary action — it
              goes light-pink and the prominent Download button takes over. */}
          <Button
            size="sm"
            variant={output ? "secondary" : "primary"}
            className={output ? "!bg-[var(--color-primary-dim)] !border-[var(--color-primary)] !text-[var(--color-primary)]" : ""}
            disabled={busy || frames.length === 0}
            onClick={openGenerate}
          >
            {sheetSet.status === "failure" && frames.length > 0
              ? "Ponów generowanie…"
              : output
                ? "Generuj ponownie…"
                : "Generuj formatki…"}
          </Button>
        </div>
      </div>
      {hasParcelInfo && (
        <SheetSetParcelsInfo
          mode={sheetSet.mode as "parcel" | "parcel_office"}
          parcels={setParcels}
          fallbackLabel={parcelNumber}
          loading={!parcels}
        />
      )}
      {error && <p className="text-[11px] font-mono text-[var(--color-primary)]">{error}</p>}
      {dirty && (
        <p className="text-[10px] font-mono uppercase tracking-wider text-amber-600">
          Niezapisane zmiany rozmieszczenia
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-3">
        {/* `isolate` contains the Leaflet/GUGiK z-indexes (up to 1000) so the
            body-portaled Modal (z-50) paints ABOVE the map — ZEDrawingMap precedent. */}
        <div className={`card overflow-hidden ${bodyHeight} min-h-[420px] isolate relative z-0`}>
          <FrameMapEditor
            srid={sheetSet.srid}
            frames={frames}
            selectedUid={selectedUid}
            viewportSizeMm={template?.viewport_size}
            numberAdjacent={sheetSet.number_adjacent_sheets}
            parcels={parcels}
            routeGeometry={routeVersion?.geometry}
            gisProjectId={routeVersion?.gis_project_id}
            onSelect={setSelectedUid}
            onRotateSelected={rotateSelected}
            onFrameCommit={(uid, patch) => {
              // Edge snapping: a frame released near a neighbour lands EXACTLY
              // edge-to-edge / grid-aligned (axis-aligned, per-axis).
              const me = frames.find((f) => f.uid === uid);
              if (!me) return;
              const snapped = snapFrameCenter(
                { center: patch.center, rotation_deg: me.rotation_deg, scale_denominator: me.scale_denominator },
                frames.filter((f) => f.uid !== uid),
                template?.viewport_size,
              );
              patchFrame(uid, { center: snapped });
            }}
            onAddFrame={addFrame}
          />
        </div>

        <div className={`card flex flex-col ${bodyHeight} min-h-[420px]`}>
          <div className="p-3 border-b border-[var(--color-border-muted)] space-y-2">
            <div className="flex items-center justify-between gap-2">
              <p className="section-label">Arkusze ({frames.length})</p>
            </div>
            <label className="block">
              <span className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
                  Szablon (papier / orientacja)
                  <Tooltip
                    content={
                      "Szablon może mieć kod QR na arkuszach: kwadrat min. 12 mm " +
                      "(zalecane 18 mm) na warstwie FORMATKA_QR. Generator podmienia go " +
                      "na każdym arkuszu na QR z danymi: zestaw, nr arkusza, wersja trasy, " +
                      "działka (TERYT), zlecenia WPW. Na warstwie tylko ten kwadrat " +
                      "(napisy są ignorowane). Brak warstwy = arkusze bez QR. " +
                      "Szczegóły: Zarządzaj → „Jak przygotować szablon”."
                    }
                  >
                    <Info size={12} className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)] cursor-help" />
                  </Tooltip>
                </span>
                <Link
                  to="/doc-generation?tab=formatki"
                  className="text-[10px] font-mono text-[var(--color-primary)] hover:underline"
                >
                  Zarządzaj →
                </Link>
              </span>
              <select
                className="input-base mt-0.5 text-xs"
                value={sheetSet.template ?? ""}
                disabled={busy}
                onChange={(e) => updateSet.mutate({ template: e.target.value || null })}
              >
                {!sheetSet.template && <option value="">— wybierz szablon —</option>}
                {templateOptions.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} · {FORMATKA_PAPER_LABEL[t.paper]}{" "}
                    {operatorOptionBadge(t.operator, connection.operator)}
                  </option>
                ))}
              </select>
              <span className="block text-[10px] font-mono text-[var(--color-text-muted)] mt-0.5 leading-tight">
                Orientacja i rozmiar arkusza wynikają z szablonu (nie z pojedynczej ramki).
              </span>
            </label>
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
                Skala zestawu (globalnie)
              </span>
              <select
                className="input-base mt-0.5 text-xs"
                value={SCALE_OPTIONS.includes(setScale) ? String(setScale) : "current"}
                disabled={busy}
                onChange={(e) => {
                  if (e.target.value !== "current") applySetScale(Number(e.target.value));
                }}
              >
                {SCALE_OPTIONS.map((s) => (
                  <option key={s} value={s}>1:{s}</option>
                ))}
                {!SCALE_OPTIONS.includes(setScale) && <option value="current">1:{setScale}</option>}
              </select>
              <span className="block text-[10px] font-mono text-[var(--color-text-muted)] mt-0.5">
                Zmienia arkusze bez własnej skali; nadpisania per arkusz zostają.
              </span>
            </label>
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
                Typ formatki (tytuł metryki)
              </span>
              <select
                className="input-base mt-0.5 text-xs"
                value={sheetSet.formatka_type}
                disabled={busy}
                onChange={(e) => updateSet.mutate({ formatka_type: e.target.value as FormatkaType })}
              >
                {(Object.keys(FORMATKA_TYPE_LABEL) as FormatkaType[]).map((t) => (
                  <option key={t} value={t}>{FORMATKA_TYPE_LABEL[t]}</option>
                ))}
              </select>
            </label>
            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={sheetSet.number_adjacent_sheets}
                disabled={busy}
                onChange={(e) => updateSet.mutate({ number_adjacent_sheets: e.target.checked })}
              />
              <span>
                Numeruj sąsiednie arkusze
                <span className="block text-[10px] font-mono text-[var(--color-text-muted)] mt-0.5 leading-tight">
                  Przy krawędziach dopisuje numer stykającego się arkusza (arkusze,
                  które się nie stykają, nie są opisywane).
                </span>
              </span>
            </label>
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            <SheetListPanel
              frames={frames}
              selectedUid={selectedUid}
              viewportSizeMm={template?.viewport_size}
              onSelect={(uid) => setSelectedUid(uid)}
              onReorder={reorderFrames}
              onRemove={removeFrame}
              onPatchFrame={patchFrame}
            />
          </div>

          {/* AD4: prominent download pinned at the bottom once the ZIP exists. */}
          {output?.url && (
            <div className="p-3 border-t border-[var(--color-border-muted)]">
              <a href={output.url} download={output.original_filename} className="block">
                <Button size="md" className="w-full">
                  <Download size={15} className="mr-1.5" /> Pobierz ZIP
                </Button>
              </a>
              <p className="text-[10px] font-mono text-[var(--color-text-muted)] text-center mt-1 truncate">
                {output.original_filename}
              </p>
            </div>
          )}
          {sheetSet.status === "failure" && sheetSet.error && (
            <div className="p-3 border-t border-[var(--color-border-muted)] space-y-2">
              <p className="text-[11px] font-mono text-[var(--color-primary)]">Błąd: {sheetSet.error}</p>
              {/* Ponowienie tuż przy powodzie — przycisk u góry strony jest daleko od
                  komunikatu i nie mówi, że to właśnie jest „spróbuj jeszcze raz". Zestaw
                  z ramkami ponawia generowanie ZIP-a (ten sam modal co zwykle); zestaw
                  bez ramek (błąd rozplanowania) ponawia rozplanowanie. */}
              {frames.length > 0 ? (
                <Button size="sm" className="w-full" disabled={busy} onClick={openGenerate}>
                  <RefreshCcw size={13} className="mr-1" /> Ponów generowanie…
                </Button>
              ) : (
                <Button size="sm" variant="secondary" className="w-full" disabled={busy} onClick={doReseed}>
                  <RefreshCcw size={13} className="mr-1" /> Ponów rozplanowanie
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {showGenerate && (
        <GenerateFormatkiModal
          sheetSet={sheetSet}
          connection={connection}
          onClose={() => setShowGenerate(false)}
        />
      )}
    </div>
  );
}
